/**
 * Terms and Conditions — English translation.
 *
 * IMPORTANT: This is an unofficial translation for convenience only.
 * The German version (AGBContentDe.tsx) is legally binding under German law.
 * When updating, keep both files in sync.
 *
 * Last updated: 2026-04-21 | Version: 1.3
 */

import { Link } from "react-router-dom";

export function AGBContentEn() {
  return (
    <>
      <p>
        <strong>Scope:</strong> Equna App and Website
      </p>
      <p>
        <strong>Operator:</strong><br />
        <strong>Patrizio Veglia</strong><br />
        Herbststr. 9<br />
        13409 Berlin, Germany
      </p>
      <p>Email: contact@equna.eu</p>

      <hr />

      <h2>1. Scope and Contracting Parties</h2>

      <h3>1.1 Scope</h3>
      <p>
        These Terms and Conditions (T&amp;C) apply to the use of the Equna App and Website (hereinafter referred to as the &ldquo;Service&rdquo;).
      </p>

      <h3>1.2 Contracting Parties</h3>
      <p>The contract for the use of the Service is concluded between:</p>
      <ul>
        <li><strong>Provider:</strong> Patrizio Veglia, Herbststr. 9, 13409 Berlin, Germany</li>
        <li><strong>User:</strong> The person who registers with Equna</li>
      </ul>

      <h3>1.3 Contract Language</h3>
      <p>The contract language is German. The English version of these T&amp;C is provided for convenience only; the German version prevails in case of discrepancies.</p>

      <hr />

      <h2>2. Registration and Conclusion of Contract</h2>

      <h3>2.1 Registration</h3>
      <p>
        The use of the Service requires registration. During registration, you must provide the following information:
      </p>
      <ul>
        <li>Email address (must be valid)</li>
        <li>Password (min. 12 characters)</li>
        <li>Display name (freely chosen)</li>
      </ul>

      <h3>2.2 Conclusion of Contract</h3>
      <p>The contract is concluded when you:</p>
      <ol>
        <li>Complete the registration form in full</li>
        <li>Accept the Privacy Policy and T&amp;C</li>
        <li>Click &ldquo;Register&rdquo;</li>
        <li>Confirm your email address (verification link)</li>
      </ol>

      <h3>2.3 Minimum Age</h3>
      <p>
        The use of the Service is <strong>only permitted for persons aged 16 and older</strong>. For persons aged 16 to 18, the consent of a legal guardian is required.
      </p>

      <hr />

      <h2>3. Services Provided</h2>

      <h3>3.1 Free Tier</h3>
      <p>
        The Service is offered as a free basic version (&ldquo;Free Tier&rdquo;) with the following features:
      </p>

      <p><strong>Expense management:</strong></p>
      <ul>
        <li>Management of shared expenses</li>
        <li>Management of child-related expenses</li>
        <li>Management of personal expenses (stored locally on the device)</li>
        <li>Expense categorisation</li>
      </ul>

      <p><strong>Budget management:</strong></p>
      <ul>
        <li>Setting monthly budgets</li>
        <li>Defining category budgets</li>
        <li>Budget progress display</li>
      </ul>

      <p><strong>Overviews &amp; analytics:</strong></p>
      <ul>
        <li>Monthly overviews with balance calculation</li>
        <li>Annual overview with month-to-month comparison</li>
        <li>Category analysis with charts</li>
        <li>Settlement calculations (who owes whom)</li>
      </ul>

      <p><strong>Groups &amp; collaboration:</strong></p>
      <ul>
        <li>Group invitations via link (valid for 24 hours)</li>
        <li>Monthly closing with partner confirmation</li>
        <li>In-app notifications</li>
      </ul>

      <p><strong>Additional features:</strong></p>
      <ul>
        <li>Upload of receipt photos (cloud storage)</li>
        <li>Management of child profiles</li>
        <li>Tutorial system for new users</li>
        <li>Data export in JSON format (GDPR-compliant)</li>
      </ul>

      <h3>3.2 Premium Tier (paid)</h3>
      <p>
        The Service may be offered for a fee as a Premium version in the future, with the following additional features:
      </p>
      <ul>
        <li><strong>Unlimited real-time updates</strong> (no delay)</li>
        <li><strong>Early budget warnings</strong> (at 80% and 90% of budget)</li>
        <li><strong>Custom budget thresholds</strong> (individual warning limits)</li>
        <li><strong>Budget groups</strong> (combine multiple categories into groups, e.g. &ldquo;fixed costs&rdquo;)</li>
        <li><strong>Extended time-range filters</strong> (custom periods in analytics)</li>
        <li><strong>Receipt retention</strong> (365 days instead of 60 in the Free Tier)</li>
        <li><strong>HTML data export</strong> (formatted, printable)</li>
        <li><strong>Extended data view</strong> (Premium GDPR view)</li>
      </ul>
      <p><strong>Prices and conditions</strong> will be announced upon introduction of the Premium Tier. Users will be informed in advance by email and in-app notification.</p>

      <h3>3.3 Availability</h3>
      <p>
        The Provider strives for high availability of the Service (target: 99% uptime). However, there is <strong>no entitlement</strong> to a specific level of availability.
      </p>
      <p>
        <strong>Maintenance:</strong> The Provider may temporarily interrupt the Service for maintenance work. Planned maintenance will be announced in advance where possible.
      </p>

      <hr />

      <h2>4. User Obligations and Responsibilities</h2>

      <h3>4.1 Accurate Information</h3>
      <p>
        The User undertakes to provide <strong>accurate and complete information</strong> during registration and use.
      </p>

      <h3>4.2 Confidentiality of Access Credentials</h3>
      <p>
        The User is obliged to <strong>keep their access credentials (password) confidential</strong> and to protect them against access by third parties.
      </p>
      <p>
        <strong>Suspected misuse:</strong> The User must inform the Provider immediately and change their password.
      </p>

      <h3>4.3 Prohibited Use</h3>
      <p>
        The User undertakes <strong>not to misuse</strong> the Service. In particular, the following are prohibited:
      </p>
      <ul>
        <li><strong>Illegal content:</strong> Uploading or sharing unlawful content</li>
        <li><strong>Harassment:</strong> Insulting, threatening, or harassing other users</li>
        <li><strong>Spam:</strong> Sending unsolicited messages or invitations</li>
        <li><strong>Hacking:</strong> Attempts to manipulate the Service or gain unauthorised access</li>
        <li><strong>Bots / automation:</strong> Use of bots or automated tools without consent</li>
        <li><strong>Reverse engineering:</strong> Decompilation or disassembly of the App</li>
      </ul>

      <h3>4.4 Liability for Content</h3>
      <p>
        The User is solely responsible for the content they upload (expenses, comments, receipt photos, etc.). The Provider does <strong>not</strong> screen this content in advance.
      </p>
      <p>
        This applies in particular to data concerning <strong>third parties</strong> (e.g. partners, children): the User ensures that they are authorised to store such data and refrains from entering sensitive data (in particular health, religion, or social data about children) into free-text fields.
      </p>

      <hr />

      <h2>5. Groups and Shared Data</h2>

      <h3>5.1 Group Functionality</h3>
      <p>
        Users can join a group to manage expenses together. Within a group, <strong>shared expenses and child-related expenses</strong> are visible to all group members. Personal expenses remain private and are stored exclusively on the User&rsquo;s device.
      </p>

      <h3>5.2 Consent to Data Sharing</h3>
      <p>
        By joining a group, the User consents to their shared expenses and child-related expenses being visible to other group members. This consent can be revoked by leaving the group.
      </p>

      <h3>5.3 Responsibility upon Leaving a Group</h3>
      <p>
        Upon leaving a group, previously recorded shared expenses are <strong>not automatically deleted</strong>, in order to preserve data integrity for the remaining group members. The name of the departing User may be anonymised upon request.
      </p>

      <h3>5.4 Disputes Between Users</h3>
      <p>
        The Provider is <strong>not a party</strong> to disputes between group members regarding expenses, distribution keys, or settlements. The App serves solely as a documentation tool. Legal claims between users (e.g. between partners or co-parents) must be resolved outside the App.
      </p>

      <hr />

      <h2>6. Limitation of Liability</h2>

      <h3>6.1 Liability for Intent and Gross Negligence</h3>
      <p>
        The Provider is liable <strong>without limitation</strong> for damages resulting from injury to life, body, or health, as well as for intent and gross negligence.
      </p>

      <h3>6.2 Liability for Slight Negligence</h3>
      <p>
        In cases of slight negligence, the Provider is <strong>only</strong> liable for breaches of material contractual obligations (cardinal obligations). In such cases, liability is limited to the <strong>foreseeable, contract-typical damage</strong>.
      </p>

      <h3>6.3 Exclusion of Liability</h3>
      <p>
        <strong>To the extent permitted by law</strong>, and subject to the liability rules in § 6.1 and § 6.2, the Provider is not liable for:
      </p>
      <ul>
        <li><strong>Data loss:</strong> Where it could have been avoided by reasonable data-backup measures by the User</li>
        <li><strong>Personal expenses:</strong> These are stored exclusively on the User&rsquo;s device and are not backed up on our servers</li>
        <li><strong>Indirect damages, lost profits, consequential damages</strong> — unless caused by intent, gross negligence, or breach of material contractual obligations (cardinal obligations)</li>
        <li><strong>Force majeure:</strong> Outages due to natural disasters, strikes, war, etc.</li>
        <li><strong>Third-party providers:</strong> Outages caused by external service providers (hosting, cloud storage)</li>
      </ul>
      <p className="text-sm text-slate-500">
        The provisions in § 6.1 (intent, gross negligence, personal injury) and § 6.2 (cardinal obligations) remain unaffected.
      </p>

      <h3>6.4 No Financial Advice</h3>
      <p>
        The Service serves exclusively for <strong>personal expense tracking</strong>. It does <strong>not constitute tax, financial, or legal advice</strong>. For tax or legal questions, please consult a qualified advisor.
      </p>

      <h3>6.5 Data Backups</h3>
      <p>
        <strong>Server-side backups:</strong> The Provider performs regular backups of data stored on our servers (shared expenses, child expenses, budgets, account data). However, <strong>no guarantee</strong> is given for the complete recoverability of data.
      </p>
      <p>
        <strong>Personal expenses:</strong> These are stored <strong>exclusively locally</strong> on the User&rsquo;s device and are <strong>not</strong> included in server-side backups. The User is responsible for backing up this data themselves.
      </p>
      <p>
        <strong>Export function:</strong> The Provider provides an export function (Profile &rarr; &ldquo;Data Access &amp; Export&rdquo;) with which users can back up their data at any time.
      </p>

      <hr />

      <h2>7. Warranty</h2>

      <h3>7.1 Free Tier</h3>
      <p>
        For the Free Tier: The Service is provided &ldquo;<strong>as is</strong>&rdquo;. <strong>No warranty</strong> is given for freedom from defects, availability, or fitness for a particular purpose.
      </p>

      <h3>7.2 Premium Tier</h3>
      <p>
        For the Premium Tier: The Provider warrants that the Service essentially corresponds to the service description. Minor deviations do <strong>not</strong> entitle the User to a reduction in price or termination.
      </p>
      <p>
        <strong>Remediation of defects:</strong> In case of defects, the Provider has the right to cure. Only after unsuccessful attempts to cure may the User reduce the price or terminate.
      </p>

      <h3>7.3 Update Obligation (§ 327f BGB)</h3>
      <p>
        Insofar as Equna qualifies as a digital product within the meaning of §§ 327 et seq. BGB, the Provider shall provide the updates necessary to maintain conformity with the contract — <strong>in particular security updates</strong> — for the period that may reasonably be expected, given the nature and purpose of the digital product. Functional enhancements are at the Provider&rsquo;s discretion.
      </p>
      <p>
        The User is obliged to install offered updates within a reasonable period. If the User fails to do so, the Provider is not liable for defects that result solely from the lack of the update — provided the Provider has informed the User about the update and the consequences of not installing it.
      </p>

      <hr />

      <h2>8. Contract Term and Termination</h2>

      <h3>8.1 Free Tier</h3>
      <p>
        The contract runs for an <strong>indefinite period</strong>. Both parties can terminate <strong>at any time without notice</strong>.
      </p>
      <p>
        <strong>Termination by User:</strong> Profile &rarr; &ldquo;Delete account&rdquo; &rarr; security confirmation
      </p>
      <p>
        <strong>Termination by Provider:</strong> By email with 14 days&rsquo; notice (without notice only in case of T&amp;C violations)
      </p>

      <h3>8.2 Premium Tier</h3>
      <p>The Premium Tier is in planning. The following conditions will be finalised upon introduction:</p>
      <p>[To be added upon introduction of the Premium Tier]</p>
      <p>Expected:</p>
      <ul>
        <li>Monthly or annual term</li>
        <li>Termination to the end of the term possible</li>
        <li>Notice period: 14 days before expiry</li>
      </ul>

      <h3>8.3 Extraordinary Termination</h3>
      <p>The Provider may terminate the contract <strong>without notice</strong> if:</p>
      <ul>
        <li>The User violates these T&amp;C (see § 4.3)</li>
        <li>The User misuses the Service</li>
        <li>Payment arrears exist (Premium Tier)</li>
      </ul>

      <hr />

      <h2>9. Data Protection</h2>

      <p>
        For the processing of personal data, our <strong>Privacy Policy</strong> applies, which you can view here:
      </p>
      <p>
        <strong>Link:</strong>{" "}
        <Link to="/datenschutz" className="text-blue-600 hover:text-blue-800 underline">
          https://equna.eu/datenschutz
        </Link>
      </p>
      <p>
        By using the Service, you consent to data processing in accordance with the Privacy Policy.
      </p>

      <hr />

      <h2>10. Changes to the T&amp;C</h2>

      <h3>10.1 Right to Amend</h3>
      <p>
        The Provider reserves the right to amend these T&amp;C. Changes will be communicated to users <strong>at least 4 weeks</strong> before they take effect, by email.
      </p>

      <h3>10.2 Distinction by Type of Amendment</h3>
      <p>
        <strong>Material changes</strong> (e.g. modifications to the main performance or consideration obligations, changes to the scope of services to the User&rsquo;s disadvantage) only take effect if the User <strong>expressly consents</strong>. If the User refuses consent or does not respond within the set period, the Provider may terminate the contract with reasonable notice; until the termination takes effect, the previous T&amp;C continue to apply.
      </p>
      <p>
        <strong>Non-material changes</strong> (e.g. editorial clarifications, adjustments to mandatory law, new features without disadvantage) will be communicated to the User at least 4 weeks before they take effect. If the User does not object within that period, the changes are deemed accepted. The Provider will expressly point out the right to object and the consequences of silence in the notice.
      </p>
      <p className="text-sm text-slate-500">
        Basis: German Federal Court of Justice, judgment of 27 April 2022 – IV ZR 485/19 (invalidity of blanket consent fictions for material contractual amendments).
      </p>

      <hr />

      <h2>11. Copyright and Usage Rights</h2>

      <h3>11.1 Provider&rsquo;s Rights</h3>
      <p>
        All rights to the App, Website, design, code, logos, and content belong to the Provider. The User does <strong>not acquire ownership</strong>, but only a <strong>non-exclusive, revocable right of use</strong>.
      </p>

      <h3>11.2 User&rsquo;s Rights</h3>
      <p>
        The User retains all rights to the content they upload (expenses, receipt photos, comments). However, the User grants the Provider a <strong>non-exclusive, worldwide licence</strong> to store and display this content (for the purpose of providing the Service).
      </p>

      <hr />

      <h2>12. Final Provisions</h2>

      <h3>12.1 Applicable Law</h3>
      <p>
        The law of the Federal Republic of Germany applies, to the exclusion of the UN Convention on Contracts for the International Sale of Goods (CISG).
      </p>

      <h3>12.2 Place of Jurisdiction</h3>
      <p>
        The place of jurisdiction is Berlin (registered office of the Provider), provided the User is a merchant, legal entity under public law, or special asset under public law.
      </p>

      <h3>12.3 Severability</h3>
      <p>
        Should individual provisions of these T&amp;C be invalid, the validity of the remaining provisions shall remain unaffected.
      </p>

      <h3>12.4 Dispute Resolution</h3>
      <p>
        The European Commission provides a platform for online dispute resolution (ODR):{" "}
        <a
          href="https://ec.europa.eu/consumers/odr"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          https://ec.europa.eu/consumers/odr
        </a>
      </p>
      <p>
        We are <strong>not obliged</strong> and <strong>not willing</strong> to participate in dispute-resolution proceedings before a consumer arbitration board.
      </p>

      <hr />

      <p className="text-sm text-slate-500 mt-8">
        <strong>Last updated:</strong> 21 April 2026<br />
        <strong>Version:</strong> 1.3 — Updates reflecting current case law and statutory changes. New: § 7.3 update obligation under § 327f BGB. Revised: § 6.3 exclusion of liability now tied to §§ 309 No. 7 BGB and cardinal obligations; § 10.2 consent fiction split in line with BGH IV ZR 485/19 into material vs. non-material changes.
      </p>

      <p className="text-sm text-slate-500">
        <strong>Contact for questions:</strong><br />
        Email: contact@equna.eu<br />
        Mail: Patrizio Veglia, Herbststr. 9, 13409 Berlin, Germany
      </p>
    </>
  );
}
